/**
 * Currency Detection Service
 * 
 * Automatically detects the appropriate currency based on user's location,
 * browser locale, and country information.
 */
import { type CurrencyCode } from '@shared/pricing';

export type { CurrencyCode };

// Country to currency mapping
const COUNTRY_CURRENCY_MAP: Record<string, CurrencyCode> = {
  // United States
  'US': 'USD',
  'USA': 'USD',
  
  // Denmark
  'DK': 'DKK',
  'DNK': 'DKK',
  'Denmark': 'DKK',
  
  // United Kingdom
  'GB': 'GBP',
  'UK': 'GBP',
  'GBR': 'GBP',
  'United Kingdom': 'GBP',
  
  // European Union countries using EUR
  'DE': 'EUR', // Germany
  'FR': 'EUR', // France
  'IT': 'EUR', // Italy
  'ES': 'EUR', // Spain
  'NL': 'EUR', // Netherlands
  'BE': 'EUR', // Belgium
  'AT': 'EUR', // Austria
  'PT': 'EUR', // Portugal
  'IE': 'EUR', // Ireland
  'FI': 'EUR', // Finland
  'LU': 'EUR', // Luxembourg
  'MT': 'EUR', // Malta
  'CY': 'EUR', // Cyprus
  'SK': 'EUR', // Slovakia
  'SI': 'EUR', // Slovenia
  'EE': 'EUR', // Estonia
  'LV': 'EUR', // Latvia
  'LT': 'EUR', // Lithuania
  'HR': 'EUR', // Croatia
  
  // Default fallback for common country name variations
  'Germany': 'EUR',
  'France': 'EUR',
  'Italy': 'EUR',
  'Spain': 'EUR',
  'Netherlands': 'EUR',
  'Belgium': 'EUR',
  'Austria': 'EUR',
  'Portugal': 'EUR',
  'Ireland': 'EUR',
  'Finland': 'EUR',
};

// Locale to currency mapping (fallback)
const LOCALE_CURRENCY_MAP: Record<string, CurrencyCode> = {
  'en-US': 'USD',
  'da-DK': 'DKK',
  'en-GB': 'GBP',
  'de-DE': 'EUR',
  'fr-FR': 'EUR',
  'it-IT': 'EUR',
  'es-ES': 'EUR',
  'nl-NL': 'EUR',
  'pt-PT': 'EUR',
  'fi-FI': 'EUR',
};

/**
 * Detect currency based on country code
 */
export function detectCurrencyFromCountry(countryCode: string): CurrencyCode {
  if (!countryCode) return 'USD';
  
  // Try exact match first
  const currency = COUNTRY_CURRENCY_MAP[countryCode.toUpperCase()];
  if (currency) return currency;
  
  // Try case-insensitive match for full country names
  const countryName = countryCode.toLowerCase();
  for (const [key, value] of Object.entries(COUNTRY_CURRENCY_MAP)) {
    if (key.toLowerCase() === countryName) {
      return value;
    }
  }
  
  return 'USD'; // Default fallback
}

/**
 * Detect currency based on browser locale
 */
export function detectCurrencyFromLocale(): CurrencyCode {
  try {
    const locale = navigator.language || navigator.languages?.[0] || 'en-US';
    return LOCALE_CURRENCY_MAP[locale] || 'USD';
  } catch {
    return 'USD';
  }
}

/**
 * Detect currency based on Intl.NumberFormat
 */
export function detectCurrencyFromIntl(): CurrencyCode {
  try {
    // Try to detect currency from system locale
    const formatter = new Intl.NumberFormat();
    const parts = formatter.formatToParts(123.45);
    
    // This is a basic approach - in practice, we'd need a more sophisticated method
    const locale = formatter.resolvedOptions().locale;
    return LOCALE_CURRENCY_MAP[locale] || 'USD';
  } catch {
    return 'USD';
  }
}

/**
 * Get timezone-based currency suggestion
 */
export function detectCurrencyFromTimezone(): CurrencyCode {
  try {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    
    // US timezones
    if (timezone.includes('America/') || timezone.includes('US/')) {
      return 'USD';
    }
    
    // European timezones
    if (timezone.includes('Europe/')) {
      if (timezone.includes('Copenhagen') || timezone.includes('Denmark')) {
        return 'DKK';
      }
      if (timezone.includes('London') || timezone.includes('Belfast')) {
        return 'GBP';
      }
      return 'EUR'; // Most European countries use EUR
    }
    
    return 'USD'; // Default fallback
  } catch {
    return 'USD';
  }
}

/**
 * Main currency detection function that combines multiple methods
 */
export function detectCurrency(userCountry?: string): CurrencyCode {
  // Priority order:
  // 1. User's stored country preference
  // 2. Browser locale
  // 3. Timezone detection
  // 4. Intl API detection
  // 5. Default to USD
  
  try {
    // If we have user's country, use that first
    if (userCountry) {
      const currencyFromCountry = detectCurrencyFromCountry(userCountry);
      if (currencyFromCountry !== 'USD' || userCountry.toUpperCase() === 'US') {
        return currencyFromCountry;
      }
    }
    
    // Try browser locale
    const localeDetection = detectCurrencyFromLocale();
    if (localeDetection !== 'USD') {
      return localeDetection;
    }
    
    // Try timezone detection
    const timezoneDetection = detectCurrencyFromTimezone();
    if (timezoneDetection !== 'USD') {
      return timezoneDetection;
    }
    
    // Fall back to Intl detection
    return detectCurrencyFromIntl();
  } catch {
    return 'USD';
  }
}

/**
 * Check if a currency code is valid
 */
export function isValidCurrency(currency: string): currency is CurrencyCode {
  return ['USD', 'DKK', 'GBP', 'EUR'].includes(currency as CurrencyCode);
}

/**
 * Get currency display name
 */
export function getCurrencyDisplayName(currency: CurrencyCode): string {
  const names: Record<CurrencyCode, string> = {
    'USD': 'US Dollar',
    'DKK': 'Danish Krone',
    'GBP': 'British Pound',
    'EUR': 'Euro'
  };
  
  return names[currency];
}

/**
 * Get currency symbol
 */
export function getCurrencySymbol(currency: CurrencyCode): string {
  const symbols: Record<CurrencyCode, string> = {
    'USD': '$',
    'DKK': 'kr',
    'GBP': '£',
    'EUR': '€'
  };
  
  return symbols[currency];
}